/**
 * Pick-Slip In-Flight Re-Sticker Pipeline (MRP-Only Mode)
 * =======================================================
 * 2026-05-14 — Replaces the legacy USN-replacement-based pipeline.
 *
 * Why MRP-only?
 *   Items on a Pick Slip are about to ship out the gate. The OLD USN
 *   already lives on the pack and the inventory; minting a NEW USN
 *   for each re-printed sticker was costly (PI/REPLACE pool drain,
 *   520 timeouts on big batches, complex revert) and bought nothing
 *   operationally — the new USN never gets re-scanned anywhere
 *   because the goods leave the building. So we keep the original
 *   USN and only refresh the MRP on the printed sticker.
 *
 *   Bonus side-effect: this also fixed the box-sticker corruption
 *   seen via the FG Pick Slip flow, since the broken USN-claim path
 *   (which fed wrong label_data into the box template) is gone.
 *
 * Two-phase printing is preserved for combos:
 *   Phase A — product (inner) stickers + non-combo singles.
 *   PROMPT  — operator swaps printer / loads box-label roll.
 *   Phase B — combo box stickers.
 *   Cancel at the prompt skips box printing but the row still
 *   counts as restickered (per user spec).
 *
 * Args (single options object):
 *   pickSlipNumber : string
 *   skuCode        : string
 *   section        : string | null
 *   staffId        : string | null
 *   staffName      : string | null
 *   usbDevice      : USBDevice
 *   printerConfig  : PRINTER_CONFIGS entry
 *   templates      : sticker templates array (from /label/templates)
 *   apiBase        : process.env.REACT_APP_BACKEND_URL          (kept for API compat — not used)
 *   sessionToken   : localStorage session_token                 (kept for API compat — not used)
 *   siteId         : 'salem' | per-section                      (kept for API compat — not used)
 *   pricingType    : state code ('TN' / 'KA' / …) for rates_master
 *   onLog?         : (msg: string) => void
 *   onPhaseTransition? : ({ comboCount }) => Promise<{ cancelled?: bool, device?: USBDevice }>
 *
 * Returns: { success, marked_count, failed_count, failures, replacements: [] }
 */
import apiClient from '@/services/apiClient';
import { noRateMessage } from '@/hooks/usePricingChannels';
import * as printerApi from '@/services/printerApi';
import { generateTsplFromLabelData } from '@/utils/tsplGenerator';

export async function runPickSlipRestickerPipeline({
  pickSlipNumber,
  skuCode,
  section,
  staffId,
  staffName,
  usbDevice,
  printerConfig,
  templates,
  // Kept in the destructured signature for backward compatibility with
  // existing call sites — the MRP-only flow no longer needs them.
  // eslint-disable-next-line no-unused-vars
  apiBase,
  // eslint-disable-next-line no-unused-vars
  sessionToken,
  // eslint-disable-next-line no-unused-vars
  siteId,
  pricingType = 'TN',
  // 2026-05-18 — When true, ignore the `already_restickered` flag and
  // reprint every picked pack for this SKU. Default false so the
  // normal CSV-revision flow keeps its idempotency guarantee.
  forceReprint = false,
  onLog = () => {},
  onPhaseStart = null,
  onPhaseTransition = null,
}) {
  const findTemplateByName = (name) =>
    templates.find((t) => t.template_name === name) || templates[0];
  const defaultTemplate = templates[0];

  // ----- PHASE 0: PREVIEW — every picked pack for this SKU on this slip
  onLog(`Fetching picked packs for SKU ${skuCode}…`);
  const previewRes = await apiClient.get('/price-revision/pick-slip-sku-preview', {
    params: {
      pick_slip_number: pickSlipNumber,
      sku_code: skuCode,
      section: section || undefined,
    },
  });
  const allPacks = previewRes.data?.packs || [];
  if (!allPacks.length) {
    throw new Error(`No picked packs found for SKU ${skuCode}.`);
  }

  // ----- PHASE 1: RESOLVE LABEL DATA -----
  // 2026-05-14 — Pull the canonical label_data + per-barcode templates
  // from /main-labels/preview-batch (same resolver the bulk Re-Sticker
  // Queue uses). Fixes the legacy issue where pick-slip-sku-preview's
  // raw item_master.label_data left quality/color/size blank on the
  // print. `pricing_type` MUST be a state code ('TN' / 'KA' / …);
  // hardcoding 'mrp' returned zero rates rows.
  const effectiveBarcodeOf = (p) => p.item_barcode || p.sku_code || null;
  const uniqueBarcodes = Array.from(new Set(
    allPacks.map(effectiveBarcodeOf).filter(Boolean)
  ));
  let resolvedMap = {};
  let serverDefaultTemplate = defaultTemplate;
  if (uniqueBarcodes.length) {
    onLog(`Resolving label data for ${uniqueBarcodes.length} barcode(s)…`);
    const batchRes = await apiClient.post('/main-labels/preview-batch', {
      barcodes: uniqueBarcodes,
      pricing_type: pricingType,
      template_id: defaultTemplate?.template_id,
    });
    const noRate = noRateMessage(batchRes.data, pricingType);
    if (noRate) throw new Error(noRate);
    resolvedMap = batchRes.data?.results || {};
    serverDefaultTemplate = batchRes.data?.template || defaultTemplate;
  }

  // Build per-pack plan, skip already-done packs. NO new USNs minted;
  // we re-use whatever `usn_codes` the pack already carries.
  // 2026-05-18 — `forceReprint=true` bypasses the already_restickered
  // skip so the operator can deliberately reprint every pack on the
  // slip (e.g. to recover from a half-printed roll or a damaged batch).
  const plans = [];
  for (const pack of allPacks) {
    if (pack.already_restickered && !forceReprint) {
      onLog(`${pack.pack_barcode}: already restickered — skipping`);
      continue;
    }
    if (pack.already_restickered && forceReprint) {
      onLog(`${pack.pack_barcode}: already restickered — FORCE REPRINT enabled, including in batch`);
    }
    const resolutionKey = effectiveBarcodeOf(pack);
    const resolved = (resolutionKey && resolvedMap[resolutionKey]) || null;
    const resolvedLabelData = resolved?.label_data || pack.label_data || {};
    const resolvedPrintTpl = resolved?.print_template || pack.print_template || 'Standard';
    const resolvedBoxTpl = resolved?.box_template || pack.box_template || '';
    const template = findTemplateByName(resolvedPrintTpl) || serverDefaultTemplate;
    const isCombo = !!(resolvedBoxTpl && resolvedBoxTpl !== '0');
    const boxTpl = isCombo ? (findTemplateByName(resolvedBoxTpl) || serverDefaultTemplate) : null;
    const existingUsnList = pack.usn_codes?.length
      ? pack.usn_codes
      : [pack.pack_barcode];
    // For combos a single shared USN drives BOTH inner and box stickers;
    // for non-combos every USN gets its own sticker (carton of N pcs).
    const usnsToPrint = isCombo ? [existingUsnList[0]] : existingUsnList;
    // 2026-05-14 — MRP is sourced from Item Master / Rates Master (via
    // /main-labels/preview-batch → resolved.label_data.mrp), NOT from
    // the CSV-uploaded `to_mrp` on the pack. The CSV is the trigger for
    // *which* packs need re-stickering; the MRP value to print is the
    // canonical master value at print-time. Falls back to pack.mrp only
    // when preview-batch returned no rates row (legacy data).
    const masterMrp = resolvedLabelData?.mrp || pack.mrp || 0;
    const labelData = { ...resolvedLabelData, mrp: masterMrp };
    const newMrpFmt = `Rs.${Number(masterMrp).toFixed(2)}`;
    plans.push({
      pb: pack.pack_barcode,
      pack,
      template,
      boxTpl,
      labelData,
      newMrpFmt,
      isCombo,
      usns: usnsToPrint,
    });
  }

  if (!plans.length) {
    return {
      success: true,
      marked_count: 0,
      failed_count: 0,
      skipped: allPacks.length,
      message: 'All packs already restickered',
      replacements: [],
    };
  }

  // ----- PHASE 1B: PRE-PRINT PROMPT -----
  // 2026-05-14 — Operator MUST explicitly confirm the product printer
  // and (optionally) override the auto-resolved product template before
  // any TSPL hits the wire. This is the "1st prompt" the user asked for:
  // alignment issues are normally template/roll-size mismatches, so we
  // surface the template choice here.
  const initialComboCount = plans.filter((p) => p.isCombo).length;
  // Suggest the most-common resolved template (mode) — usually 'Standard'.
  const templateModeOf = (arr, key) => {
    const counts = {};
    for (const x of arr) {
      const name = x[key]?.template_name;
      if (name) counts[name] = (counts[name] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
  };
  const suggestedProductTemplate = templateModeOf(plans, 'template');
  const suggestedBoxTemplate = initialComboCount > 0
    ? templateModeOf(plans.filter((p) => p.isCombo), 'boxTpl')
    : null;
  let activeDevice = usbDevice;
  if (typeof onPhaseStart === 'function') {
    const startResp = await onPhaseStart({
      phase: 'before_product',
      packCount: plans.length,
      comboCount: initialComboCount,
      suggestedProductTemplate,
      suggestedBoxTemplate,
      availableTemplates: templates.map((t) => t.template_name).filter(Boolean),
    });
    if (startResp && startResp.cancelled) {
      onLog('Print cancelled by operator before product phase.');
      return {
        success: true,
        marked_count: 0,
        failed_count: 0,
        skipped: allPacks.length,
        cancelled: true,
        replacements: [],
      };
    }
    if (startResp && startResp.device) activeDevice = startResp.device;
    // Apply per-plan template overrides AFTER plans have been built so
    // they take effect on the Phase-2 TSPL render.
    if (startResp && startResp.productTemplate) {
      const tpl = findTemplateByName(startResp.productTemplate);
      if (tpl) for (const pl of plans) pl.template = tpl;
    }
    if (startResp && startResp.boxTemplate) {
      const tpl = findTemplateByName(startResp.boxTemplate);
      if (tpl) for (const pl of plans) if (pl.isCombo) pl.boxTpl = tpl;
    }
  }

  // ----- PHASE 2: BUILD TWO TSPL BLOBS (product vs box) -----
  // 2026-05-14 — Two-phase combo: PRODUCT roll first, prompt, then BOX
  // roll. Non-combo single-USN packs always print in the PRODUCT phase.
  // `generateTsplFromLabelData(labelData, template, usnCode, mrpFormatted,
  // displayUsn, printerConfig, templateType, pairNumber)` — 8th positional
  // arg is `pairNumber`; `displayUsn` stays null so the USN itself prints.
  let innerBlob = '';
  let boxBlob = '';
  let comboCount = 0;
  for (const pl of plans) {
    if (pl.isCombo) {
      const sharedUsn = pl.usns[0];
      const pairNum = pl.pack.pair_number || 1;
      innerBlob += generateTsplFromLabelData(
        pl.labelData, pl.template, sharedUsn, pl.newMrpFmt,
        null, printerConfig, pl.template?.template_type, pairNum,
      );
      boxBlob += generateTsplFromLabelData(
        pl.labelData, pl.boxTpl, sharedUsn, pl.newMrpFmt,
        null, printerConfig, pl.boxTpl?.template_type, pairNum,
      );
      comboCount += 1;
    } else {
      for (const usn of pl.usns) {
        innerBlob += generateTsplFromLabelData(
          pl.labelData, pl.template, usn, pl.newMrpFmt, null,
          printerConfig, pl.template?.template_type,
        );
      }
    }
  }
  if (!innerBlob && !boxBlob) {
    throw new Error('No TSPL commands generated');
  }

  // ----- PHASE 3A: PRODUCT STICKERS -----
  if (innerBlob) {
    onLog(`Streaming product stickers (${plans.length} pack(s))…`);
    try {
      await printerApi.sendToUSBPrinter(activeDevice, innerBlob);
    } catch (e) {
      throw new Error(
        `Printer error during product-sticker phase. `
        + `Nothing was marked — re-run when the printer is healthy. `
        + (e.message || '')
      );
    }
  }

  // ----- PHASE 3B: BOX STICKERS (combo only) -----
  if (boxBlob && comboCount > 0) {
    let boxDevice = activeDevice;
    if (typeof onPhaseTransition === 'function') {
      const transitionResp = await onPhaseTransition({
        phase: 'after_product_before_box',
        comboCount,
        message: `Product stickers printed. Switch printer / load box-label roll, then click Continue to print box stickers.`,
        currentDevice: activeDevice,
        suggestedBoxTemplate,
        availableTemplates: templates.map((t) => t.template_name).filter(Boolean),
      });
      if (transitionResp && transitionResp.cancelled) {
        onLog(
          `Box-sticker phase cancelled by operator after product phase. `
          + `${comboCount} combo box sticker(s) NOT printed. All packs will still be marked restickered (per spec).`
        );
        boxBlob = '';
      } else {
        if (transitionResp && transitionResp.device) boxDevice = transitionResp.device;
        // 2026-05-14 — Operator may override the box template at the
        // Phase-B prompt. Re-render the box blob with the new template
        // so alignment / coordinates come from the operator's pick.
        if (transitionResp && transitionResp.boxTemplate) {
          const newBoxTpl = findTemplateByName(transitionResp.boxTemplate);
          if (newBoxTpl) {
            boxBlob = '';
            for (const pl of plans) {
              if (!pl.isCombo) continue;
              const sharedUsn = pl.usns[0];
              const pairNum = pl.pack.pair_number || 1;
              boxBlob += generateTsplFromLabelData(
                pl.labelData, newBoxTpl, sharedUsn, pl.newMrpFmt,
                null, printerConfig, newBoxTpl?.template_type, pairNum,
              );
            }
          }
        }
      }
    }
    if (boxBlob) {
      onLog(`Streaming ${comboCount} box sticker(s)…`);
      try {
        await printerApi.sendToUSBPrinter(boxDevice, boxBlob);
      } catch (e) {
        onLog(`Box-sticker print error: ${e.message || e}. Packs will still be marked.`);
      }
    }
  }

  // ----- PHASE 4: MARK ON-PICK-SLIP (MRP-only, no USN replacement) -----
  onLog(`Marking ${plans.length} pack(s) as restickered (MRP-only)…`);
  const markRes = await apiClient.post('/price-revision/mark-restickered-on-pick-slip', {
    pick_slip_number: pickSlipNumber,
    sku_code: skuCode,
    packs: plans.map((pl) => ({
      pack_barcode: pl.pb,
      usn_codes: pl.usns,
    })),
    section: section || null,
    staff_id: staffId || null,
    staff_name: staffName || null,
    mrp_only: true,
  });

  return {
    success: markRes.data?.success,
    marked_count: markRes.data?.marked_count || 0,
    failed_count: markRes.data?.failed_count || 0,
    failures: markRes.data?.failures || [],
    replacements: [],  // MRP-only — no USN replacements happen.
    skipped: allPacks.length - plans.length,
  };
}
